import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Text } from "@/components/ui/typography"
import { cn } from "@/lib/utils"
import { semanticTokens } from "@supabank/config/theme"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

type Intent = "test" | "invite"

type SubmitResponse = { success: boolean; id?: string; error?: string }

type Variant = "full" | "compact"

type WaitlistFormProps = {
  variant?: Variant
  className?: string
  inputClassName?: string
  submitLabel?: string
  source?: string
  onSuccess?: (response: SubmitResponse) => void
  onError?: (message: string) => void
  autoRedirect?: boolean
}

type FormState = {
  email: string
  name: string
  intent: Intent
  agreed: boolean
  loading: boolean
  error: string | null
}

const defaultState: FormState = {
  email: "",
  name: "",
  intent: "test",
  agreed: false,
  loading: false,
  error: null,
}

const waitlistEndpoint = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/domain-hub?act=waitlist.submit&v=1`

export function WaitlistForm({
  variant = "full",
  className,
  inputClassName,
  submitLabel,
  source = "landing",
  onSuccess,
  onError,
  autoRedirect = false,
}: WaitlistFormProps) {
  const { t } = useTranslation("common")
  const navigate = useNavigate()
  const [state, setState] = useState<FormState>({
    ...defaultState,
    agreed: variant === "compact" ? true : false,
  })
  const intentOptions = useMemo(
    () => [
      { value: "test" satisfies Intent, label: t("waitlistForm.intentTest") },
      { value: "invite" satisfies Intent, label: t("waitlistForm.intentInvite") },
    ],
    [t]
  )
  const finalSubmitLabel = submitLabel || t("waitlistForm.submit")

  useEffect(() => {
    if (variant === "compact" && state.agreed === false) {
      setState((prev) => ({ ...prev, agreed: true }))
    }
  }, [variant, state.agreed])

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setState((prev) => ({ ...prev, [key]: value }))
  }

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setState((prev) => ({ ...prev, error: null }))

    const trimmedEmail = state.email.trim()
    const emailPattern = /.+@.+\..+/

    if (!trimmedEmail) {
      const message = t("waitlistForm.errors.emailRequired")
      setField("error", message)
      onError?.(message)
      return
    }

    if (!emailPattern.test(trimmedEmail)) {
      const message = t("waitlistForm.errors.emailInvalid")
      setField("error", message)
      onError?.(message)
      return
    }

    if (!state.agreed) {
      const message = t("waitlistForm.errors.privacyRequired")
      setField("error", message)
      onError?.(message)
      return
    }

    setState((prev) => ({ ...prev, loading: true }))

    try {
      const response = await fetch(waitlistEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          email: trimmedEmail,
          name: variant === "full" ? state.name : undefined,
          intent: state.intent,
          source,
          utm: {
            source: new URLSearchParams(window.location.search).get("utm_source"),
            medium: new URLSearchParams(window.location.search).get("utm_medium"),
            campaign: new URLSearchParams(window.location.search).get("utm_campaign"),
          },
        }),
      })

      if (response.status === 429) {
        const message = t("waitlistForm.errors.tooManyRequests")
        setField("error", message)
        onError?.(message)
        return
      }

      if (!response.ok) {
        const body = await response.json().catch(() => ({})) as SubmitResponse
        const message = body.error || t("waitlistForm.errors.submissionFailed")
        setField("error", message)
        onError?.(message)
        return
      }

      const data = (await response.json()) as SubmitResponse

      if (data.success) {
        onSuccess?.(data)
        if (autoRedirect) {
          navigate("/waitlist")
        } else {
          setState((prev) => ({ ...defaultState, agreed: variant === "compact" }))
        }
        if (typeof window !== "undefined" && (window as any).gtag) {
          ; (window as any).gtag("event", "waitlist_submit", {
            intent: state.intent,
            event_category: "engagement",
          })
        }
      } else {
        const message = data.error || t("waitlistForm.errors.submissionFailed")
        setField("error", message)
        onError?.(message)
      }
    } catch (error) {
      // Debug logging only in e2e mode
      if (typeof window !== 'undefined' && window.localStorage.getItem('e2e_debug') === 'true') {
        console.error("[WaitlistForm] submit", error)
      }
      const message = t("waitlistForm.errors.networkError")
      setField("error", message)
      onError?.(message)
    } finally {
      setState((prev) => ({ ...prev, loading: false }))
    }
  }

  return (
    <form onSubmit={submit} className={className} noValidate>
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="waitlist-email" className={semanticTokens.typography.label}>
            {t("waitlistForm.emailLabel")}
          </Label>
          <Input
            id="waitlist-email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            required
            value={state.email}
            onChange={(event) => setField("email", event.target.value)}
            placeholder={t("waitlistForm.emailPlaceholder")}
            disabled={state.loading}
            className={inputClassName}
            aria-required="true"
            aria-describedby="waitlist-email-hint"
          />
          <Text id="waitlist-email-hint" variant="caption" className={semanticTokens.fg.muted}>
            {t("waitlistForm.emailHint")}
          </Text>
        </div>

        {variant === "full" && (
          <>
            <div className="space-y-2">
              <Label htmlFor="waitlist-name" className={semanticTokens.typography.label}>
                {t("waitlistForm.nameLabel")}
              </Label>
              <Input
                id="waitlist-name"
                type="text"
                autoComplete="name"
                autoCapitalize="words"
                value={state.name}
                onChange={(event) => setField("name", event.target.value)}
                placeholder={t("waitlistForm.namePlaceholder")}
                disabled={state.loading}
              />
            </div>

            <fieldset className="space-y-3">
              <legend className={cn(semanticTokens.typography.label, semanticTokens.fg.default)}>
                {t("waitlistForm.intentLabel")}
              </legend>
              <RadioGroup
                value={state.intent}
                onValueChange={(value) => setField("intent", value as Intent)}
                className="grid gap-3"
                aria-label="Waitlist intent"
              >
                {intentOptions.map((option) => (
                  <Label
                    key={option.value}
                    htmlFor={`intent-${option.value}`}
                    className="flex items-center gap-3 rounded-md border border-input bg-background p-3 text-sm focus-within:ring-2 focus-within:ring-[rgba(0,87,255,0.2)] focus-within:ring-offset-2"
                  >
                    <RadioGroupItem id={`intent-${option.value}`} value={option.value} />
                    <span className="text-foreground">{option.label}</span>
                  </Label>
                ))}
              </RadioGroup>
            </fieldset>

            <div className="flex items-start gap-3">
              <Checkbox
                id="waitlist-policy"
                checked={state.agreed}
                onCheckedChange={(value) => setField("agreed", Boolean(value))}
                disabled={state.loading}
              />
              <Label htmlFor="waitlist-policy" className={cn(semanticTokens.typography.bodySmall, semanticTokens.fg.muted)}>
                {t("waitlistForm.agreePrivacy")}
              </Label>
            </div>
          </>
        )}

        {state.error && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{state.error}</AlertDescription>
          </Alert>
        )}

        <Button
          type="submit"
          size="lg"
          className={cn("w-full rounded-xl px-6", semanticTokens.focus.ring)}
          disabled={state.loading}
        >
          {state.loading ? t("waitlistForm.submitting") : finalSubmitLabel}
        </Button>
      </div>
    </form>
  )
}
